/* eslint-disable no-shadow, object-curly-newline */

// Dependencies
import { getTTFB, getFCP, getLCP, getFID, getCLS } from 'web-vitals'

/**
 * Core Web Vitals thresholds.
 * @see https://github.com/GoogleChromeLabs/web-vitals-report/blob/71b0879334798c732f460945ded5267cab5a36bf/src/js/analytics.js#L50-L55
 * @see https://github.com/GoogleChrome/lighthouse/blob/8ff570d3c322313865795f58f11752ad07b2d0ff/lighthouse-core/audits/server-response-time.js#L25-L28
 *
 * @type {Array}
 */
const thresholds = {
  CLS: [0.1, 0.25],
  FCP: [1800, 3000],
  FID: [100, 300],
  LCP: [2500, 4000],
  TTFB: [100, 600]
}

/**
 * Get Core Web Vitals rating for each metric.
 * @see https://github.com/GoogleChromeLabs/web-vitals-report/blob/71b0879334798c732f460945ded5267cab5a36bf/src/js/analytics.js#L57-L65
 *
 * @param  {Number}  value
 * @param  {Array}   thresholds
 * @return {String}
 */
function getRating(value, thresholds) {
  if (value > thresholds[1]) {
    return 'POOR'
  }
  if (value > thresholds[0]) {
    return 'NI'
  }
  return 'GOOD'
}

/**
 * Get node path as a CSS selector.
 * @see https://github.com/GoogleChromeLabs/web-vitals-report/blob/71b0879334798c732f460945ded5267cab5a36bf/src/js/analytics.js#L67-L83
 *
 * @param  {Node} node
 * @return {String}
 */
function getNodePath(node) {
  try {
    let name = node.nodeName.toLowerCase()
    if (name === 'body') {
      return 'html>body'
    }
    if (node.id) {
      return `${name}#${node.id}`
    }
    if (node.className && node.className.length) {
      name += `.${[...node.classList.values()].join('.')}`
    }
    return `${getNodePath(node.parentElement)}>${name}`
  } catch (error) {
    return '(error)'
  }
}

/**
 * Get debug info for a web vitals performance entry.
 * @see https://github.com/GoogleChromeLabs/web-vitals-report/blob/71b0879334798c732f460945ded5267cab5a36bf/src/js/analytics.js#L85-L122
 *
 * @param  {Number}  value
 * @param  {Array}   thresholds
 * @return {String}
 */
function getDebugInfo(name, entries = []) {
  const firstEntry = entries[0]
  const lastEntry = entries[entries.length - 1]

  switch (name) {
    case 'LCP':
      if (lastEntry) {
        return getNodePath(lastEntry.element)
      }
      break
    case 'FID':
      if (firstEntry) {
        const { name } = firstEntry
        // Report interactions with the `google-signin2` element as that,
        // not any of the sub-elements.
        if (firstEntry.target && firstEntry.target.closest('#google-signin2')) {
          return `${name}(#google-signin2)`
        }
        return `${name}(${getNodePath(firstEntry.target)})`
      }
      break
    case 'CLS':
      if (entries.length) {
        const largestShift = entries.reduce((a, b) => {
          return a && a.value > b.value ? a : b
        }, [])
        if (largestShift && largestShift.sources) {
          const largestSource = largestShift.sources.reduce((a, b) => {
            return a.node &&
              a.previousRect.width * a.previousRect.height >
                b.previousRect.width * b.previousRect.height
              ? a
              : b
          }, [])
          if (largestSource) {
            return getNodePath(largestSource.node)
          }
        }
      }
      break
    default:
      return '(not set)'
  }
}

/**
 * Track web vitals data via Tagular.
 * @see https://github.com/GoogleChrome/web-vitals#readme
 * @see https://app.make.rvapps.io/schemas/sch_1ofhDFwJqLfXE91RzyPPIFhuyt6
 *
 * @param  {Object}  metric { delta, entries, id, name, value }
 * @return {void}
 */
function sendToAnalytics(metric) {
  const { entries, name, value } = metric

  if (typeof window.tagular !== 'undefined') {
    window.tagular('beam', {
      '@type': 'core.WebVitals.v1',
      debugInfo: getDebugInfo(name, entries),
      metricName: String(name),
      rating: getRating(value, thresholds[name]),
      value: Number(value)
    })
  }
}

// Get vitals in supported browsers.
if (
  window.performance &&
  typeof window.performance.getEntriesByName === 'function'
) {
  getTTFB(sendToAnalytics)
  getFCP(sendToAnalytics)
  getLCP(sendToAnalytics)
  getFID(sendToAnalytics)
  getCLS(sendToAnalytics)
}
